import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { GEOLOCATION_CONFIG } from '@/config/constants';
import { permissionStatusAtom, showPermissionsOverlayAtom } from '@/lib/atoms';
import { cameraOffInPhoneSettingsMessage, requestPermissions } from '@/lib/permissions';
import { BrowserPromptHint } from './BrowserPromptHint';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useAtom } from 'jotai';
import { Camera, MapPin, ShieldAlert, TriangleAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';

/** What the user has to do: a warning, not an app error, so amber rather than the red error card. */
function PermissionNotice({ children }: { children: ReactNode }) {
  return (
    <Alert className="border-amber-300 bg-amber-50 text-amber-900">
      <TriangleAlert />
      <AlertDescription className="text-amber-900">{children}</AlertDescription>
    </Alert>
  );
}

export const PermissionsOverlay = () => {
  const [showOverlay, setShowOverlay] = useAtom(showPermissionsOverlayAtom);
  const [isRequesting, setIsRequesting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [status, setStatus] = useAtom(permissionStatusAtom);

  const handleRequestPermissions = async () => {
    setIsRequesting(true);
    setFailed(false);

    try {
      const result = await requestPermissions();

      if (!result.granted) {
        setStatus(result);
        setFailed(true);
      } else {
        setShowOverlay(false);
      }
    } catch {
      setFailed(true);
    } finally {
      setIsRequesting(false);
    }
  };

  if (!showOverlay) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="permissions-title"
    >
      <div className="relative w-full max-w-2xl mx-4 p-8 bg-background rounded-lg shadow-2xl animate-in zoom-in-95 duration-200">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ShieldAlert />
            </EmptyMedia>
            <EmptyTitle id="permissions-title">
              <Trans>Permissions Required</Trans>
            </EmptyTitle>
            <EmptyDescription>
              {GEOLOCATION_CONFIG.ENABLED ? (
                <Trans>To take photos of your vehicle, we need access to your camera and location.</Trans>
              ) : (
                <Trans>To take photos of your vehicle, we need access to your camera.</Trans>
              )}
            </EmptyDescription>
          </EmptyHeader>

          <EmptyContent>
            <div className="w-full space-y-3 text-left">
              <p className="font-medium text-gray-900">
                <Trans>Missing permissions:</Trans>
              </p>
              <ul className="space-y-2">
                {!status?.camera && (
                  <li className="flex items-center gap-3 text-gray-700">
                    <Camera className="w-5 h-5 text-red-500" />
                    <span>
                      <Trans>Camera access</Trans>
                    </span>
                  </li>
                )}
                {GEOLOCATION_CONFIG.ENABLED && !status?.location && (
                  <li className="flex items-center gap-3 text-gray-700">
                    <MapPin className="w-5 h-5 text-red-500" />
                    <span>
                      <Trans>Location access</Trans>
                    </span>
                  </li>
                )}
              </ul>
            </div>

            {status?.cameraOffInPhoneSettings ? (
              <PermissionNotice>{cameraOffInPhoneSettingsMessage()}</PermissionNotice>
            ) : (
              failed && (
                <PermissionNotice>
                  {GEOLOCATION_CONFIG.ENABLED
                    ? t`Unable to access permissions. Please enable camera and location access in your browser or phone settings.`
                    : t`Unable to access permissions. Please enable camera access in your browser or phone settings.`}
                </PermissionNotice>
              )
            )}

            <Button onClick={handleRequestPermissions} disabled={isRequesting} className="w-full" size="lg">
              {isRequesting ? <Trans>Requesting permissions...</Trans> : <Trans>Request Permissions</Trans>}
            </Button>
          </EmptyContent>
        </Empty>
      </div>
      <BrowserPromptHint waiting={isRequesting} />
    </div>
  );
};
